import React, { useEffect, useState } from 'react';
import { Button } from 'antd';
import { RcFile } from 'antd/lib/upload';
import moment, { Moment } from 'moment';
import { formatDateDashYMDTimeHMS } from 'components/utils/constants/formatters';
import { UploadInvoiceFieldsProps } from 'types/requests';
import { Input, RenderIf } from 'components/components';
import { CreateInvoiceType } from 'packages/invoice_repository/types';
import { TrashCanIcon } from 'assets/icons';
import showNotification from 'services/utils/showNotification';
import { postServiceRequestDocuments } from 'packages/service_request_repository/api';
import './UploadInvoiceFields.scss';

/**
 * UploadInvoiceFields Component
 *
 * Enhanced functionality for uploading multiple invoices:
 * - Supports multiple invoice uploads using existing single-invoice API
 * - Sequential upload with proper error handling
 * - Optional parallel upload capability
 * - Insurance policy upload for health insurance requests
 * - Progress indication and user feedback
 * - Backward compatibility with single invoice format
 */
// Invoice item type definition
type InvoiceItem = {
  id: string;
  invoiceFile: string | Blob;
  invoiceNumber: string;
  invoiceDescription: string;
  invoiceDate: Moment | null;
  invoicePrice: number;
  insurancePolicy?: string | Blob;
  isInsurancePolicy?: boolean;
};

const UploadInvoiceFields = (props: UploadInvoiceFieldsProps) => {
  const { serviceRequest, actions: { setShowInvoiceFields, updateServiceRequest, createInvoice } } = props;

  // Initialize with one invoice item
  const [invoices, setInvoices] = useState<InvoiceItem[]>([{
    id: Date.now().toString(),
    invoiceFile: '',
    invoiceNumber: '',
    invoiceDescription: '',
    invoiceDate: null,
    invoicePrice: serviceRequest.totalPrice,
    insurancePolicy: '',
    isInsurancePolicy: false,
  }]);

  const [isHealthInsurance, setIsHealthInsurance] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (serviceRequest.itemList[0] === 'Health Insurance') {
      setIsHealthInsurance(true);
      // Update all invoices to have insurance policy requirement
      setInvoices((prev) => prev.map((invoice) => ({
        ...invoice,
        isInsurancePolicy: true,
      })));
    }
  }, [serviceRequest.itemList[0]]);

  const getTotalPrice = () => invoices.reduce((sum, invoice) => sum + (invoice.invoicePrice || 0), 0);

  const getRemainingAmount = () => {
    const totalUsed = getTotalPrice();
    const remaining = serviceRequest.totalPrice - totalUsed;
    return Math.max(0, remaining);
  };

  const addNewInvoice = () => {
    const remainingAmount = getRemainingAmount();
    const newInvoice: InvoiceItem = {
      id: Date.now().toString(),
      invoiceFile: '',
      invoiceNumber: '',
      invoiceDescription: '',
      invoiceDate: null,
      invoicePrice: remainingAmount,
      insurancePolicy: '',
      isInsurancePolicy: isHealthInsurance,
    };
    setInvoices((prev) => [...prev, newInvoice]);
  };

  const removeInvoice = (invoiceId: string) => {
    if (invoices.length > 1) {
      setInvoices((prev) => prev.filter((invoice) => invoice.id !== invoiceId));
    }
  };

  const updateInvoice = (
    invoiceId: string,
    field: keyof InvoiceItem,
    value: string | Blob | Moment | number | boolean | null,
  ) => {
    setInvoices((prev) => prev.map((invoice) => (invoice.id === invoiceId
      ? { ...invoice, [field]: value }
      : invoice)));
  };

  const beforeInvoiceUpload = (file: RcFile, invoiceId: string) => {
    updateInvoice(invoiceId, 'invoiceFile', file as Blob);
    return false;
  };

  const beforeInsurancePolicyUpload = (file: RcFile, invoiceId: string) => {
    updateInvoice(invoiceId, 'insurancePolicy', file as Blob);
    updateInvoice(invoiceId, 'isInsurancePolicy', false);
    return false;
  };

  const isFormValid = () => {
    const totalPrice = getTotalPrice();
    if (totalPrice !== serviceRequest.totalPrice) return false;

    return invoices.every((invoice) => invoice.invoiceNumber !== ''
      && invoice.invoiceDescription !== ''
      && invoice.invoiceFile !== ''
      && invoice.invoicePrice > 0
      && invoice.invoiceDate !== null
      && (!isHealthInsurance || !invoice.isInsurancePolicy));
  };

  const uploadInvoice = async (invoiceData: {
    invoice: string | Blob;
    invoiceNumber: string;
    invoiceDate: string;
    totalPrice: number;
    description: string;
  }, index: number): Promise<{ success: boolean; error?: string }> => {
    const invoiceName = invoices.length === 1 ? 'Invoice' : `Invoice ${index + 1}`;

    try {
      const invoicePayload: CreateInvoiceType = {
        serviceRequestId: serviceRequest.id,
        invoiceNumber: invoiceData.invoiceNumber,
        invoiceDate: invoiceData.invoiceDate,
        invoiceAmount: invoiceData.totalPrice,
        invoiceFile: invoiceData.invoice as Blob,
        description: invoiceData.description,
      };

      createInvoice({ data: invoicePayload });

      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      showNotification(`Failed to upload ${invoiceName}: ${errorMessage}`, true, 500);
      return { success: false, error: errorMessage };
    }
  };

  const uploadInsurancePolicies = async (): Promise<void> => {
    if (!isHealthInsurance) return;

    for (const invoice of invoices) {
      if (invoice.insurancePolicy) {
        try {
          const result = await postServiceRequestDocuments({
            _document: invoice.insurancePolicy,
            _name: 'Insurance Policy',
            _service_request_id: serviceRequest.id,
          });

          if (result.error) {
            const errorMessage = typeof result.error === 'string' ? result.error : result.error?.message || 'Unknown error';
            showNotification(`Failed to upload insurance policy: ${errorMessage}`, true, result.httpStatus);
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          showNotification(`Failed to upload insurance policy: ${errorMessage}`, true, 500);
        }
      }
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      const totalPrice = getTotalPrice();
      if (totalPrice !== serviceRequest.totalPrice) {
        showNotification(`Total invoice amount (${totalPrice}) must equal exactly ${serviceRequest.totalPrice}`, true, 400);
        return;
      }

      await uploadInsurancePolicies();

      const invoicesData = invoices.map((invoice) => ({
        invoice: invoice.invoiceFile,
        invoiceNumber: invoice.invoiceNumber,
        description: invoice.invoiceDescription,
        totalPrice: invoice.invoicePrice,
        invoiceDate: moment(invoice.invoiceDate).format(formatDateDashYMDTimeHMS),
      }));

      const uploadResults = [];
      for (let i = 0; i < invoicesData.length; i++) {
        const result = await uploadInvoice(invoicesData[i], i);
        uploadResults.push(result);
      }

      const allSuccessful = uploadResults.every((result) => result.success);

      if (allSuccessful) {
        updateServiceRequest({
          id: serviceRequest.id,
          totalPrice: getTotalPrice(),
        });

        showNotification(`Successfully uploaded ${invoicesData.length} invoice(s)`, false, 200);
      } else {
        const failedCount = uploadResults.filter((result) => !result.success).length;
        showNotification(`${failedCount} invoice(s) failed to upload. Please try again.`, true, 500);
      }
    } catch (error) {
      if (error instanceof Error) {
        showNotification(`Something went wrong: ${error.message}`, true, 500);
      } else {
        showNotification(`Something went wrong: ${error}`, true, 500);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderInvoiceCard = (invoice: InvoiceItem, index: number) => (
    <div key={invoice.id} className="invoice-card">
      <div className="invoice-card__header">
        <h4>{invoices.length === 1 ? 'Invoice' : `Invoice ${index + 1}`}</h4>
        {invoices.length > 1 && (
          <Button
            type="text"
            onClick={() => removeInvoice(invoice.id)}
            size="small"
            icon={<TrashCanIcon />}
          />
        )}
      </div>

      <Input label="Invoice number" layout="vertical" required>
        <Input.InputField
          placeholder="Enter Invoice Number"
          name={`invoiceNumber_${invoice.id}`}
          value={invoice.invoiceNumber}
          onChange={({ target: { value } }) => updateInvoice(invoice.id, 'invoiceNumber', value)}
        />
      </Input>

      <Input label="Invoice Description" layout="vertical" required>
        <Input.InputField
          placeholder="Enter Invoice Description"
          name={`invoiceDescription_${invoice.id}`}
          value={invoice.invoiceDescription}
          onChange={({ target: { value } }) => updateInvoice(invoice.id, 'invoiceDescription', value)}
        />
      </Input>

      <div className="invoice-card__row">
        <div className="invoice-card__field">
          <Input label="Invoice Date" layout="vertical" required>
            <Input.DatePicker
              placeholder="Enter Invoice Date"
              name={`invoiceDate_${invoice.id}`}
              value={invoice.invoiceDate}
              onChange={(value) => updateInvoice(invoice.id, 'invoiceDate', value)}
            />
          </Input>
        </div>

        <div className="invoice-card__field">
          <Input label="Total Price" layout="vertical" required>
            <Input.InputField
              placeholder="8000"
              name={`totalPrice_${invoice.id}`}
              value={invoice.invoicePrice}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateInvoice(invoice.id, 'invoicePrice', Number(e.target.value))}
            />
          </Input>
          {getTotalPrice() !== serviceRequest.totalPrice && (
            <div className="invoice-card__error">
              The sum of Total Prices must equal exactly
              {' '}
              {serviceRequest.totalPrice}
            </div>
          )}
        </div>
      </div>

      <Input label="Invoice pdf" layout="vertical" required>
        <Input.DraggerUpload
          beforeUpload={(file) => beforeInvoiceUpload(file, invoice.id)}
          previewType="image"
          name={`invoice_${invoice.id}`}
          accept=".pdf"
          isRoundableButton
        />
      </Input>

      <RenderIf condition={isHealthInsurance}>
        <Input label="Insurance Policy" layout="vertical" required>
          <Input.DraggerUpload
            beforeUpload={(file) => beforeInsurancePolicyUpload(file, invoice.id)}
            previewType="image"
            name={`insurancepolicy_${invoice.id}`}
            accept=".pdf"
            isRoundableButton
          />
        </Input>
      </RenderIf>
    </div>
  );

  return (
    <div className="upload-invoice-fields">
      <div className="upload-invoice-fields__title">
        <h3>Invoice(s)</h3>
      </div>
      {invoices.map((invoice, index) => renderInvoiceCard(invoice, index))}

      <div className="upload-invoice-fields__add-invoice">
        <Button
          type="text"
          onClick={addNewInvoice}
          style={{ marginBottom: '20px' }}
        >
          + Add Invoice
        </Button>
      </div>

      <div className="upload-invoice-fields__buttons">
        <Button className="secondary-button md" onClick={() => setShowInvoiceFields({ data: false })}>
          Cancel
        </Button>
        <Button
          className="primary-button md"
          onClick={handleSubmit}
          disabled={!isFormValid() || isSubmitting}
          loading={isSubmitting}
        >
          {isSubmitting ? 'Uploading...' : 'Deliver'}
        </Button>
      </div>
    </div>
  );
};

export default UploadInvoiceFields;
